import { useCallback, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Radio,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import HelpIcon from "@mui/icons-material/Help";
import { useQuery } from "react-query";
import BtnCarregarDados from "../../BtnCarregarDados";
import useMobileScreen from "../../../lib/useMobileScreen";
import useDebounce from "../../../lib/useDebounce";
import DialogAppBar from "../../DialogAppBar";
import axiosClient from "../../../lib/axios";

export default function DialogContentSelectGeneric<T extends SelectableItem = any>({
  defaultValue,
  onChange,
  handleClose,
  multiple = false,
  queryPath,
  extraParams,
  title,
  possuiBtnLimpar = false,
  getItemKey,
  getItemLabel,
}: DialogContentSelectGenericProps<T>) {
  const telaMobile = useMobileScreen();
  const [limit, setLimit] = useState(20);
  const [palavraChave, setPalavraChave] = useState("");
  const [selecionados, setSelecionados] = useState<any[]>(
    multiple ? [...(defaultValue as any[])] : [defaultValue as T]
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedKeyword = useDebounce(palavraChave);

  const { data, isFetching, isLoading } = useQuery({
    queryKey: [`select-generic-${queryPath}`, debouncedKeyword, limit, extraParams],
    queryFn: () => axiosClient.get(queryPath, {
      params: {
        limit,
        page: 1,
        pesquisar: debouncedKeyword,
        ...extraParams,
      },
    }),
    onSuccess: () => {
      inputRef.current?.focus();
    },
    cacheTime: 0,
  });

  const { reqContent, dataContent } = useMemo(
    () => ({
      reqContent: data?.data,
      dataContent: data?.data.data,
    }),
    [data]
  );

  const isEmptyItem = useCallback((item: T) => {
    return (item?.id === 0 || item?.id === "") && item?.nome === "";
  }, []);

  // Simplificar a lógica do handleClick
  const handleClick = useCallback((item: T) => {
    if (multiple) {
      setSelecionados(prev => {
        if (prev.length === 1 && isEmptyItem(prev[0])) {
          return [item];
        }

        const itemKey = getItemKey(item);
        const index = prev.findIndex(i => getItemKey(i) === itemKey);

        return index === -1
          ? [...prev, item]
          : prev.filter((_, i) => i !== index);
      });
    } else {
      setSelecionados([item]);
    }
  }, [multiple, getItemKey, isEmptyItem]);

  const handleSubmit = useCallback(() => {
    if (!multiple) {
      onChange(selecionados[0]);
      return;
    }

    onChange(selecionados);
  }, [onChange, selecionados, multiple]);

  const handleLimpar = () => {
    setSelecionados([]);
    onChange(null);
    handleClose();
  };

  return (
    <>
      {telaMobile && <DialogAppBar {...{ handleClose }} title={title} />}
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ mt: { xs: 2, md: 0 } }}>
        <Box display="flex" alignItems="center" gap={2} py={2}>
          <TextField
            value={palavraChave}
            fullWidth
            inputRef={inputRef}
            label="Pesquisar"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                )
              },
              htmlInput: { sx: { textTransform: "uppercase", tabIndex: 0 } }
            }}
            onChange={(e) => setPalavraChave(e.target.value)}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {isLoading && (
          <Box display="flex" justifyContent="center" my={2}>
            <CircularProgress />
          </Box>
        )}

        {!isLoading && (
          <FormGroup>
            <Box
              display="flex"
              overflow="auto"
              height={{ xs: 550, md: 350 }}
              flexDirection="column"
            >
              {dataContent && dataContent.length <= 0 && (
                <Box
                  display="flex"
                  justifyContent="center"
                  paddingY={2}
                  gap={1}
                  color="gray"
                >
                  <HelpIcon />
                  <Typography>Nenhum resultado encontrado</Typography>
                </Box>
              )}

              {multiple && dataContent && dataContent.length > 0 &&
                <FormControlLabel
                  sx={{
                    backgroundColor: (theme) => theme.palette.action.hover,
                    borderRadius: 1,
                    pl: 1,
                  }}
                  control={
                    <Checkbox
                      checked={
                        dataContent.every((item: T) =>
                          selecionados.some((selected) => getItemKey(selected) === getItemKey(item))
                        )
                      }
                      indeterminate={
                        dataContent.some((item: T) =>
                          selecionados.some((selected) => getItemKey(selected) === getItemKey(item))
                        ) &&
                        !dataContent.every((item: T) =>
                          selecionados.some((selected) => getItemKey(selected) === getItemKey(item))
                        )
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelecionados((prev) => [
                            ...prev,
                            ...dataContent.filter(
                              (item: T) =>
                                !prev.some((selected) => getItemKey(selected) === getItemKey(item))
                            ),
                          ]);
                        } else {
                          setSelecionados((prev) =>
                            prev.filter(
                              (selected) =>
                                !dataContent.some((item: T) => getItemKey(selected) === getItemKey(item))
                            )
                          );
                        }
                      }}
                    />
                  }
                  label={
                    <Tooltip title="Seleciona ou desmarca todos os itens visíveis">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography fontWeight="bold">Selecionar Todos</Typography>
                      </Box>
                    </Tooltip>
                  }
                />
              }

              {dataContent &&
                dataContent.length > 0 &&
                dataContent.map((i: any, index: number) => {
                  const isChecked = selecionados.some(
                    (item: T) => getItemKey(item) === getItemKey(i)
                  );
                  return (
                    <>
                      <FormControlLabel
                        key={index}
                        onChange={() => handleClick(i)}
                        control={
                          multiple ? (
                            <Checkbox checked={isChecked} />
                          ) : (
                            <Radio checked={isChecked} />
                          )
                        }
                        sx={{ mb: 0, pl: 1 }}
                        label={
                          <Box>
                            <Typography>{`${getItemLabel(i)}`}</Typography>
                          </Box>
                        }
                      />
                    </>
                  );
                })}
              <BtnCarregarDados
                {...{ limit, isFetching }}
                total={reqContent.total}
                action={() => setLimit((prevState) => prevState + 20)}
              />
            </Box>
          </FormGroup>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={possuiBtnLimpar ? handleLimpar : handleClose}
        >
          {possuiBtnLimpar ? "Limpar" : "Cancelar"}
        </Button>
        <Button
          autoFocus
          variant="contained"
          onClick={handleSubmit}
          disabled={selecionados.length === 1 && isEmptyItem(selecionados[0])}
        >
          Selecionar {multiple ? `(${selecionados.length})` : ""}
        </Button>
      </DialogActions>
    </>
  );
}

interface SelectableItem {
  id?: number | string;
  nome?: string;
  [key: string]: any;
}

interface DialogContentSelectGenericProps<T extends SelectableItem = any> {
  retornar_processos?: boolean;
  defaultValue: T | T[] | null;
  handleClose: () => void;
  onChange: (arg: T | T[] | null) => void;
  multiple?: boolean;
  title: string;
  queryPath: string;
  extraParams?: { [key: string]: any };
  getItemKey: (item: T) => string | number;
  getItemLabel: (item: T) => string | number;
  possuiBtnLimpar?: boolean;
}